import {Injectable} from '@angular/core';
import {SharedService} from "./sharedService";
import {PagerDataObject} from "../entities/pagerDataObject";
import {catchError, map, tap} from 'rxjs/operators';
import {HttpClient, HttpHeaders} from "@angular/common/http";
import {Indicator, IndicatorDataset} from "../entities/indicator";
import { Observable, of } from 'rxjs';

const httpOptions = {
  headers: new HttpHeaders({'Content-Type': 'application/json'})
};

@Injectable()
export class IndicatorService {

  constructor(private http: HttpClient, private sharedService: SharedService) {
  }

  export$(query): Observable<Blob> {
    const httpOptionsExcel = {
      headers: new HttpHeaders({'Accept': 'application/vnd.ms-excel', 'Content-Type': 'application/vnd.ms-excel'}),
      responseType: 'blob' as 'json'
    };
    return this.http.get<any>(this.sharedService.apiUrl('api/indicators/export?', query), httpOptionsExcel).pipe(
        map((res: Response) => res),
        catchError(this.sharedService.handleError<any>('download excel'))
    );
  }

  getData(query: string): Observable<PagerDataObject> {
    return this.http.get<PagerDataObject>(this.sharedService.apiUrl('api/indicators', query !== null ? ('/' + query) : ''));
  }

  getDataRisks(query: string): Observable<PagerDataObject> {
    return this.http.get<PagerDataObject>(this.sharedService.apiUrl('api/risk', query !== null ? query : ''));
  }

  getYears(): Observable<any> {
    return this.http.get<PagerDataObject>(this.sharedService.apiUrl('api/indicators/years', ''));
  }

  add(indicator: Indicator): Observable<Indicator> {
    return this.http.post<Indicator>(this.sharedService.apiUrl('api/indicators/add'), indicator, httpOptions).pipe(
        tap((indicator: Indicator) => this.log(`added indicator w/ id=${indicator.id}`)),
        catchError(this.handleError<Indicator>('add'))
    );
  }

  getIndicatorResult(query: string): Observable<PagerDataObject> {
    return this.http.get<PagerDataObject>(this.sharedService.apiUrl('api/indicators/indicator_result', query !== null ? query : ''));
  }

  getIndicator(id: string): Observable<Indicator> {
    return this.http.get<Indicator>(this.sharedService.apiUrl('api/indicators/' + id));
  }

  runIndicator(indicator: Indicator): Observable<any> {
    return this.http.post<any>(this.sharedService.apiUrl('api/indicators/run_indicator'), indicator, httpOptions).pipe(
        tap((result: any) => this.log(`indicator executed w/ id=${indicator.id}`)),
        catchError(this.handleError<any>('indicator'))
    );
  }

  delete(id:any): Observable<Indicator> {
    return this.http.delete<Indicator>(this.sharedService.apiUrl(`api/indicators/${id}`), httpOptions).pipe(
        tap((indicator: Indicator) => this.sharedService.log(`deleted indicator w/ id=${id}`)),
        catchError(this.sharedService.handleError<any>('deleteIndicator'))
    );
  }

  columns: Array<any> = [
    {key: 'view', style: {'flex': '0 0 100px', 'width': '1%'}, button: true},
    {name: 'Indicator Name', tag: 'INDICATOR.NAME', key: 'name', filtering: true, sorting: true, sortingOrder: 'asc'},
  ];

  columnsResults: Array<any> = [
    {name: 'Tax id', tag: 'RISK-RESULT.TAX_ID', key: 'match', filtering: true, sorting: true, sortingOrder: 'asc'},
    {name: 'Company Name', tag: 'RISK-RESULT.COMPANY_NAME', key: 'companyName', filtering: true, sorting: true, sortingOrder: 'asc'},
    {name: 'Score', tag: 'RISK-RESULT.SCORE', key: 'score', filtering: true, sorting: true, sortingOrder: 'asc', isNumber: true, coloring: [{3: '#3ba52d'}, {8: '#f6c945'}, {100: '#f60e12'}]},
  ];

  getColumnsIndicatorRun(datasets?: IndicatorDataset[]): Array<any> {
    const baseCols = [
      {name: 'Match', tag: 'INDICATOR.MATCH', key: 'match', filtering: true, sorting: true, sortingOrder: 'asc'},
      {name: 'Company Name', tag: 'INDICATOR.COMPANY_NAME', key: 'companyName', filtering: true, sorting: true, sortingOrder: 'asc'},
    ];

    const valueCols = datasets && datasets.length > 0
        ? datasets.map((ds, i) => ({
          name: ds.name || ('Value ' + (i + 1)),
          tag: ds.name || ('INDICATOR.VALUE' + (i + 1)),
          key: 'value' + i,
          filtering: true,
          sorting: true,
          sortingOrder: 'asc',
          isNumber: true
        }))
        : [
          {name: 'Value1', tag: 'INDICATOR.VALUE1', key: 'value0', filtering: true, sorting: true, sortingOrder: 'asc', isNumber: true},
          {name: 'Value2', tag: 'INDICATOR.VALUE2', key: 'value1', filtering: true, sorting: true, sortingOrder: 'asc', isNumber: true},
        ];

    const endCols = [
      {name: 'Difference Absolute', tag: 'INDICATOR.DIFFERENCE', key: 'difference', filtering: true, sorting: true, sortingOrder: 'asc', isNumber: true},
      {name: '% Difference Percentage', tag: 'INDICATOR.DIFFERENCE_PERCENTAGE', key: 'percentage', filtering: true, sorting: true, sortingOrder: 'asc', isNumber: true},
    ];

    return [...baseCols, ...valueCols, ...endCols];
  }

  getMetaIndicatorRunInfo(datasets?: IndicatorDataset[]): string[] {
    const base = ['match', 'companyName'];
    const values = datasets && datasets.length > 0
        ? datasets.map((_, i) => 'value' + i)
        : ['value0', 'value1'];
    return [...base, ...values, 'difference', 'percentage'];
  }

  getMetaInfo(): string[] {
    return ['name'];
  }

  getColumns(): string[] {
    return this.columns;
  }

  getMetaResultsInfo(): string[] {
    return ['match', 'companyName', 'score'];
  }

  getColumnsResults(): string[] {
    return this.columnsResults;
  }

  private handleError<T>(operation = 'operation', result?: T) {
    return (error: any): Observable<T> => {
      console.error(error);
      this.log(`${operation} failed: ${error.message}`);
      return of(result as T);
    };
  }

  private log(message: string) {
  }
}